import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"
import { ResponsiveImageFragment } from "../../types.graphql"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { useRafLoop } from "react-use"
import { Button } from "@components/ui/Button"
import { twMerge } from "tailwind-merge"
import { clamp } from "motion/react"

export type ParallaxCarouselType = HTMLDivElement & {
  setOffsetXPercent: (index: number) => void
  setOverallOpacity: (opacity: number) => void
}

export const ParallaxCarousel = forwardRef<
  ParallaxCarouselType,
  {
    images: { aspect: number | null; srcSet: string | null }[]
    className?: string
    controls?: boolean
    onChange?: (index: number) => void
    onInactive?: (index: number) => void
    startIndex?: number
    inactiveWidth?: number
    activeWidth?: number
    useCurrentOffset?: boolean
    align?: "left" | "center" | "right"
    offsetXPercent?: number
    fadePreviousItems?: boolean
    ease?: number
    opacityEase?: number
    firstRenderImmediate?: boolean
    clampToRightEdge?: boolean
  }
>(
  (
    {
      images,
      className,
      controls = true,
      onChange,
      onInactive,
      startIndex = 0,
      inactiveWidth = 0.65,
      activeWidth = undefined,
      useCurrentOffset = true,
      align = "left",
      offsetXPercent = 0,
      fadePreviousItems = false,
      ease = 0.09,
      opacityEase = 0.25,
      firstRenderImmediate = false,
      clampToRightEdge = true,
    },
    usableRef,
  ) => {
    const ref = useRef<HTMLDivElement>(null)
    const [current, setCurrent] = useState(startIndex)
    const index = useRef(startIndex)
    const lastTick = useRef(0)
    const spacerRef = useRef<HTMLDivElement | null>(null)
    const elements = useRef<HTMLDivElement[]>([])
    const innerRefs = useRef<HTMLImageElement[]>([])
    const innerImageRefs = useRef<HTMLImageElement[]>([])
    const firstRender = useRef(true)
    const offsetXPerc = useRef(offsetXPercent)
    const overallOpacity = useRef(1)
    const positions = useRef<
      Array<{
        w: number
        wEased: number
        x: number
        xEased: number
        ixEased: number
        blur: number
        blurEased: number
        opacity: number
        opacityEased: number
      }>
    >([])
    const drag = useRef({
      down: false,
      dragged: false,
      start: {
        x: 0,
        y: 0,
      },
      delta: {
        x: 0,
        y: 0,
      },
      current: {
        xLerp: 0,
        yLerp: 0,
      },
    })

    useImperativeHandle(
      usableRef,
      () =>
        Object.assign(ref.current!, {
          setOffsetXPercent: (index: number) => {
            offsetXPerc.current = index
          },
          setOverallOpacity: (opacity: number) => {
            overallOpacity.current = opacity
          },
        }),
      [ref],
    )

    const next = () => {
      const total = elements.current?.length || 1
      index.current = index.current + 1 < total ? index.current + 1 : 0
      setCurrent(index.current)
    }

    const previous = () => {
      const total = elements.current?.length || 1
      index.current = index.current - 1 >= 0 ? index.current - 1 : total - 1
      setCurrent(index.current)
    }

    const pointerDown = (e: PointerEvent) => {
      if (!controls) return
      if (e.target instanceof HTMLElement && ref.current?.contains(e.target)) {
        const d = drag.current
        d.start.x = e.clientX
        d.start.y = e.clientY
        d.down = true
        d.dragged = false
      }
    }

    const pointerMove = (e: PointerEvent) => {
      if (!controls) return
      const d = drag.current
      if (!d.down) return
      d.delta.x = e.clientX - d.start.x
      d.delta.y = e.clientY - d.start.y

      if (Math.abs(d.delta.x) > 10) {
        d.dragged = true
      }

      // snap
      if (Math.abs(d.delta.x) > window.innerWidth * 0.15) {
        if (d.delta.x < 0) next()
        else previous()
        pointerUp()
      }
    }

    const pointerUp = () => {
      if (!controls) return
      const d = drag.current
      if (!d.down) return
      d.down = false
      d.delta.x = 0
      d.delta.y = 0
    }

    const preventDefault = (e: Event) => {
      e.preventDefault()
    }

    const canClick = (e: Event) => {
      if (drag.current.dragged) {
        e.preventDefault()
        e.stopPropagation()
      }
    }

    const onTouch = (e: TouchEvent) => {
      if (!controls) return
      if (drag.current.dragged && drag.current.down) {
        e.preventDefault()
        e.stopPropagation()
      }
    }

    // make sure there's always at least 5 images
    const useImages = images //images.length < 5 ? [...images, ...images] : images
    const total = useImages.length

    useEffect(() => {
      if (!ref.current) return
      spacerRef.current = ref.current?.querySelector(".image-spacer")
      elements.current = Array.from(ref.current.querySelectorAll(".image-outer"))
      innerRefs.current = Array.from(ref.current.querySelectorAll(".image-inner"))
      innerImageRefs.current = Array.from(ref.current.querySelectorAll(".image-inner-main"))
    }, [images])

    useRafLoop(() => {
      if (!ref.current || !spacerRef.current) return

      if (Date.now() - lastTick.current < 1000 / 60) return

      drag.current.current.xLerp += (drag.current.delta.x - drag.current.current.xLerp) * 0.2
      drag.current.current.yLerp += (drag.current.delta.y - drag.current.current.yLerp) * 0.2

      const space = spacerRef.current.offsetWidth

      let x = 0
      let currentX = 0
      let maxW = 0
      elements.current.forEach((outer, i) => {
        const inner = innerRefs.current[i]
        if (!positions.current[i])
          positions.current[i] = {
            w: 0,
            wEased: 0,
            x: 0,
            xEased: -ref.current?.offsetWidth! || -window.innerWidth,
            ixEased: 0,
            blur: 0,
            blurEased: 0,
            opacity: 1,
            opacityEased: 1,
          }
        positions.current[i].w =
          index.current === i
            ? activeWidth
              ? inner.offsetHeight * activeWidth
              : inner.offsetWidth
            : inner.offsetWidth * inactiveWidth
        positions.current[i].x = x
        if (i === index.current) currentX = x
        x += positions.current[i].w + space
        maxW += positions.current[i].w + space

        if (i === total - 1) {
          maxW -= space
        }
      })

      const alignOffset =
        (align === "center" ? ref.current.offsetWidth / 2 - maxW / 2 : 0) +
        ref.current.offsetWidth * offsetXPerc.current

      maxW += alignOffset

      if (maxW > ref.current.offsetWidth && clampToRightEdge) {
        // prevent images leaving a gap on the right
        currentX = Math.max(0, Math.min(maxW - ref.current.offsetWidth, currentX))
      }

      elements.current.forEach((outer, i) => {
        const pos = positions.current[i]
        const inner = innerRefs.current[i]
        const innerPos = (inner.offsetWidth - pos.w) / 2

        // start off at centre?
        if (align === "center" && pos.xEased === 0) {
          pos.xEased = maxW / 2
        }

        const x = pos.x - (useCurrentOffset ? currentX : 0) + alignOffset + drag.current.current.xLerp

        pos.wEased += (pos.w - pos.wEased) * ease
        pos.xEased += (x - pos.xEased) * ease
        pos.ixEased += (innerPos - pos.ixEased) * ease

        pos.blur = i === index.current ? Math.min(20.0, Math.abs(pos.x - currentX - pos.xEased) * 0.02) * 0.5 : 0
        pos.blurEased += (pos.blur - pos.blurEased) * opacityEase

        pos.opacity = (fadePreviousItems ? (i >= index.current ? 1 : 0) : 1) * overallOpacity.current
        pos.opacityEased += (pos.opacity - pos.opacityEased) * opacityEase

        if (firstRender.current) {
          if (firstRenderImmediate) {
            pos.wEased = pos.w
            pos.xEased = x
            pos.ixEased = innerPos
            pos.blurEased = pos.blur
            pos.opacityEased = pos.opacity
          }
        }

        if (Math.abs(x - pos.xEased) <= 1) {
          if (onInactive) onInactive(i)
        }

        pos.wEased = Math.round(pos.wEased * 1000) / 1000
        pos.xEased = Math.round(pos.xEased * 1000) / 1000
        pos.ixEased = Math.round(pos.ixEased * 1000) / 1000
        pos.blurEased = Math.round(pos.blurEased * 1000) / 1000
        pos.opacityEased = Math.round(pos.opacityEased * 1000) / 1000

        // pos.xEased = pos.xEased % positions.current[images.length - 1].x
      })

      elements.current.forEach((outer, i) => {
        const pos = positions.current[i]
        const inner = innerRefs.current[i]
        const image = innerImageRefs.current[i]

        inner.style.opacity = `${pos.opacityEased}`
        outer.style.width = `${pos.wEased}px`
        outer.style.transform = `translateX(${pos.xEased}px)`
        inner.style.transform = `translateX(${-pos.ixEased}px)`
        // image.style.filter = `blur(${pos.blurEased}px)`
      })

      firstRender.current = false
    })

    useEffect(() => {
      index.current = current
      if (onChange) onChange(current)
    }, [current])

    useEffect(() => {
      window.addEventListener("pointerdown", pointerDown, { passive: true })
      window.addEventListener("pointermove", pointerMove, { passive: true })
      window.addEventListener("pointerup", pointerUp, { passive: true })
      window.addEventListener("pointercancel", pointerUp, { passive: true })
      window.addEventListener("pointerleave", pointerUp, { passive: true })
      ref.current?.addEventListener("touchmove", onTouch, false)

      return () => {
        window.removeEventListener("pointerdown", pointerDown)
        window.removeEventListener("pointermove", pointerMove)
        window.removeEventListener("pointerup", pointerUp)
        window.removeEventListener("pointercancel", pointerUp)
        window.removeEventListener("pointerleave", pointerUp)
        ref.current?.removeEventListener("touchmove", onTouch)
      }
    }, [])

    return (
      <div ref={ref} className={twMerge("relative w-full overflow-hidden", className)}>
        <div className="image-spacer h-full w-4 absolute left-0 top-0 pointer-events-none opacity-0" />
        {useImages.map((image, i) => (
          <div className="image-outer h-full absolute top-0 left-0 overflow-hidden select-none" key={i}>
            <div
              className="relative h-full image-inner opacity-0"
              style={{
                aspectRatio: activeWidth ? `${activeWidth} / 1` : image.aspect!,
              }}
            >
              <ResponsiveImage
                {...image}
                className="image-inner-bg absolute h-full w-full left-0 top-0"
                resizer={false}
              />
              <ResponsiveImage
                {...image}
                className="image-inner-main relative h-full w-full"
                resizer={false}
                objectFit="cover"
              />
            </div>
          </div>
        ))}
        {images.length > 1 && controls && (
          <div className="absolute right-6 bottom-6 flex gap-2">
            <Button icon="chevronLeft" variant="secondary" onClick={() => previous()} />
            <Button icon="chevronRight" variant="secondary" onClick={() => next()} />
          </div>
        )}
      </div>
    )
  },
)
